import PropTypes from "prop-types";
import { useTheme } from "styled-components";

import { roundOff } from "utils";
import { BarChart } from "components/Chart";
import { unixToDateTime } from "utils/date";
import { AverageTradePerformanceChartPlaceholder } from "./AverageTradePerformanceChartPlaceholder";

const ChartTooltip = ({ active = false, payload = [] }) => {
  if (active && payload && payload.length) {
    return (
      <div className="chart-tooltip">
        <span className="datetime">
          {unixToDateTime(payload[0].payload.name)}
        </span>
        {payload.map((p, i) => {
          return (
            <div key={i} className="label" style={{ color: p.color }}>
              <span># of trades: </span>
              <span>
                {/* Check if a number has a decimal place */}
                {p.value % 1 != 0 ? (
                  <span>{roundOff(p.value * 100)}%</span>
                ) : (
                  <span>{p.value}</span>
                )}
              </span>
              <div>% gain: {roundOff(p.payload.percent)}%</div>
            </div>
          );
        })}
      </div>
    );
  }

  return null;
};

ChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
};

const XAxisLabel = (props) => {
  const theme = useTheme();

  const { x, y, payload } = props;
  return (
    <text
      x={x}
      y={y}
      dy={16}
      dx={9}
      textAnchor="end"
      fill={theme.heading.color.secondary}
      fontSize={10}
      fontWeight={400}
    >
      {`${payload.value}%`}
    </text>
  );
};

XAxisLabel.propTypes = {
  payload: PropTypes.object,
  x: PropTypes.number,
  y: PropTypes.number,
};

const AverageTradePerformanceChart = ({
  data = [],
  height = "280px",
  barDataKey = "frequency",
  margin = {
    top: 30,
    left: 0,
  },
  isLoading,
}) => {
  const theme = useTheme();

  const dataChart = data.map((entry) => {
    entry.color = entry.percent < 0 ? "#FF8282" : "#25FF7C";

    return entry;
  });

  if (isLoading) {
    return <AverageTradePerformanceChartPlaceholder height={height} />;
  }

  if (data.length === 0) {
    return null;
  }

  return (
    <>
      <div className="desktop-view">
        <div style={{ width: "100%", height }}>
          <BarChart
            data={dataChart}
            xAxisProps={{
              tick: <XAxisLabel />,
              domain: ["auto", "auto"],
              type: "number",
              dataKey: "percent",
              tickCount: 12,
            }}
            yAxisProps={{ dataKey: barDataKey, tickCount: 50, hide: true }}
            barDataKey={barDataKey}
            barChartProps={{
              margin,
              barGap: 10,
              barCategoryGap: 8,
            }}
            tooltipContent={ChartTooltip}
            barProps={{
              label: {
                position: "top",
                fill: theme.heading.color.secondary,
                fontSize: 8,
                fontWeight: 400,
              },
            }}
          />
        </div>
      </div>
      <div className="mobile-view">
        <div style={{ width: "100%", height }}>
          <BarChart
            data={dataChart}
            xAxisProps={{
              tick: <XAxisLabel />,
              domain: ["auto", "auto"],
              type: "number",
              dataKey: "percent",
              tickCount: 12,
            }}
            yAxisProps={{ dataKey: barDataKey, tickCount: 50, hide: true }}
            barDataKey={barDataKey}
            barChartProps={{
              margin: {
                left: -10,
                top: 10,
              },
              barGap: 10,
              barCategoryGap: 5,
            }}
            tooltipContent={ChartTooltip}
            barProps={{
              label: {
                position: "top",
                fill: theme.heading.color.secondary,
                fontSize: 8,
                fontWeight: 400,
              },
            }}
          />
        </div>
      </div>
    </>
  );
};

AverageTradePerformanceChart.propTypes = {
  data: PropTypes.array,
  height: PropTypes.string,
  barDataKey: PropTypes.string,
  margin: PropTypes.object,
  isLoading: PropTypes.bool,
};

export { AverageTradePerformanceChart };
